import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

/**
 * Client-side renderers for the selected-entities screening-change report.
 * The entity service returns the structured detail (added / removed / modified
 * hits per entity); these helpers turn that JSON into a downloadable PDF or CSV.
 */

export interface EntityChangeFieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface EntityChangeMatch {
  recordId: string;
  name: string | null;
  list: string | null;
  source: string | null;
  sourceCountry?: string | null;
  country: string | null;
  address?: string | null;
  entityType?: string | null;
  program?: string | null;
  relatedEntity?: string | null;
  identification?: string | null;
  status?: string | null;
  sourceUrl?: string | null;
}

export interface EntityChangeModifiedMatch extends EntityChangeMatch {
  fieldChanges: EntityChangeFieldChange[];
}

export interface EntityChangeDetail {
  databaseEntityId: string;
  entityName: string | null;
  country: string | null;
  riskLevel: string | null;
  currentScreenedAt: string | null;
  previousScreenedAt: string | null;
  isBaseline: boolean;
  status: 'changed' | 'no_change_in_range' | 'never_screened';
  added: EntityChangeMatch[];
  removed: EntityChangeMatch[];
  modified: EntityChangeModifiedMatch[];
}

export interface ChangeReportMeta {
  databaseName: string;
  from: string;
  to: string;
}

const fmtVal = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item)))
      .join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const fmtDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '-';

const statusLabel = (detail: EntityChangeDetail): string => {
  if (detail.status === 'never_screened') return 'Never screened';
  if (detail.status === 'no_change_in_range') return 'No changes in this period';
  return 'Changed';
};

/** Only genuine comparisons belong in a changes report. First-screen baselines are excluded. */
export const getReportableEntityChanges = (
  entities: EntityChangeDetail[],
): EntityChangeDetail[] =>
  entities
    .map((entity) => ({
      ...entity,
      modified: entity.modified.filter((hit) => hit.fieldChanges.length > 0),
    }))
    .filter(
      (entity) =>
        !entity.isBaseline &&
        entity.status === 'changed' &&
        entity.added.length + entity.modified.length + entity.removed.length > 0,
    );

const withExtension = (fileName: string, ext: string): string =>
  fileName.toLowerCase().endsWith(`.${ext}`) ? fileName : `${fileName}.${ext}`;

const PDF_COLOR = {
  primary: [72, 149, 239] as [number, number, number],
  text: [15, 23, 42] as [number, number, number],
  secondary: [71, 85, 105] as [number, number, number],
  muted: [148, 163, 184] as [number, number, number],
  divider: [226, 232, 240] as [number, number, number],
  card: [248, 250, 252] as [number, number, number],
  green: [22, 163, 74] as [number, number, number],
  amber: [217, 119, 6] as [number, number, number],
  red: [220, 38, 38] as [number, number, number],
};

const fieldLabel = (field: string): string =>
  field
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const changeSummary = (entities: EntityChangeDetail[]): {
  changed: number;
  added: number;
  modified: number;
  removed: number;
} =>
  entities.reduce(
    (summary, entity) => ({
      changed: summary.changed + 1,
      added: summary.added + entity.added.length,
      modified: summary.modified + entity.modified.length,
      removed: summary.removed + entity.removed.length,
    }),
    { changed: 0, added: 0, modified: 0, removed: 0 },
  );

/** Build a branded per-entity report. Exported separately so rendering can be tested. */
export const buildEntityChangesPdf = (
  entities: EntityChangeDetail[],
  meta: ChangeReportMeta,
): jsPDF => {
  const changedEntities = getReportableEntityChanges(entities);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - marginX * 2;
  const totals = changeSummary(changedEntities);
  const generatedAt = new Date();
  let y = 70;

  // Cover - follows the BITE report family: blue rail, wordmark, large title,
  // ruled metadata, and a short statement of purpose.
  doc.setFillColor(...PDF_COLOR.primary);
  doc.rect(0, 0, 18, pageHeight, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...PDF_COLOR.text);
  doc.text('BITE', marginX, 58);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF_COLOR.secondary);
  doc.text('Trade Compliance Intelligence', marginX, 72);

  doc.setFontSize(13);
  doc.text('Entity Screening', marginX, 245);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(36);
  doc.setTextColor(...PDF_COLOR.text);
  doc.text('Changes Report', marginX, 285);

  const coverRows = [
    ['DATABASE', meta.databaseName],
    ['REPORTING PERIOD', `${fmtDate(meta.from)} - ${fmtDate(meta.to)}`],
    ['ENTITIES WITH CHANGES', String(changedEntities.length)],
    ['GENERATED', generatedAt.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })],
    ['PREPARED BY', 'BITE Entity Screening'],
  ];
  let coverY = 350;
  coverRows.forEach(([label, value]) => {
    doc.setDrawColor(...PDF_COLOR.divider);
    doc.line(marginX, coverY, pageWidth - marginX, coverY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_COLOR.secondary);
    doc.text(label, marginX, coverY + 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(...PDF_COLOR.text);
    doc.text(value, marginX + 142, coverY + 18);
    coverY += 34;
  });
  doc.line(marginX, coverY, pageWidth - marginX, coverY);
  doc.setDrawColor(...PDF_COLOR.primary);
  doc.setLineWidth(2.5);
  doc.line(pageWidth / 2 - 30, coverY + 30, pageWidth / 2 + 30, coverY + 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(...PDF_COLOR.text);
  doc.text(
    'A clear record of watchlist hits that were added, updated, or removed during the selected period.',
    marginX,
    coverY + 55,
    { maxWidth: contentWidth },
  );

  doc.addPage();
  y = 72;

  const sectionHeader = (number: string, title: string): void => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...PDF_COLOR.primary);
    doc.text(`${number} `, marginX, y);
    doc.setTextColor(...PDF_COLOR.text);
    doc.text(title, marginX + 24, y);
    doc.setDrawColor(...PDF_COLOR.primary);
    doc.setLineWidth(1.2);
    doc.line(marginX, y + 8, pageWidth - marginX, y + 8);
    y += 28;
  };

  sectionHeader('01', 'Executive Summary');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...PDF_COLOR.text);
  const summaryText = `${totals.changed} ${totals.changed === 1 ? 'entity has' : 'entities have'} changes compared with an earlier screening. Only added hits, removed hits, and fields that changed within an existing hit are included below.`;
  doc.text(summaryText, marginX, y, { maxWidth: contentWidth, lineHeightFactor: 1.35 });
  y += 42;

  const cards = [
    [String(totals.changed), 'ENTITIES CHANGED'],
    [String(totals.added), 'HITS ADDED'],
    [String(totals.modified), 'HITS UPDATED'],
    [String(totals.removed), 'HITS REMOVED'],
  ];
  const gap = 9;
  const cardWidth = (contentWidth - gap * 3) / 4;
  cards.forEach(([value, label], index) => {
    const x = marginX + index * (cardWidth + gap);
    doc.setFillColor(...PDF_COLOR.card);
    doc.setDrawColor(...PDF_COLOR.divider);
    doc.roundedRect(x, y, cardWidth, 62, 4, 4, 'FD');
    doc.setFillColor(...PDF_COLOR.primary);
    doc.rect(x, y, cardWidth, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.setTextColor(...PDF_COLOR.primary);
    doc.text(value, x + 10, y + 27);
    doc.setFontSize(7.2);
    doc.setTextColor(...PDF_COLOR.secondary);
    doc.text(label, x + 10, y + 47);
  });
  y += 88;
  sectionHeader('02', 'Entity Change Details');

  const ensureSpace = (needed: number): void => {
    if (y + needed > pageHeight - 58) {
      doc.addPage();
      y = 70;
    }
  };

  const advanceAfterTable = (): void => {
    const last = (doc as any).lastAutoTable;
    if (last && typeof last.finalY === 'number') {
      y = last.finalY + 16;
    }
  };

  changedEntities.forEach((entity, index) => {
    if (index > 0) y += 10;

    const entityName = entity.entityName || entity.databaseEntityId;
    const entityNameLines = doc.splitTextToSize(entityName, contentWidth - 28);
    const entityNameExtraHeight = Math.max(0, entityNameLines.length - 1) * 14;
    const entityCardHeight = 78 + entityNameExtraHeight;
    ensureSpace(entityCardHeight + 24);
    doc.setFillColor(...PDF_COLOR.card);
    doc.setDrawColor(...PDF_COLOR.divider);
    doc.roundedRect(marginX, y - 16, contentWidth, entityCardHeight, 4, 4, 'FD');
    doc.setFillColor(...PDF_COLOR.primary);
    doc.rect(marginX, y - 16, 4, entityCardHeight, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_COLOR.text);
    doc.text(entityNameLines, marginX + 14, y + 5);

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...PDF_COLOR.secondary);
    const metaLine = [
      entity.country ? `Country: ${entity.country}` : null,
      entity.riskLevel ? `Risk: ${entity.riskLevel}` : null,
      `Compared: ${fmtDate(entity.previousScreenedAt)} to ${fmtDate(entity.currentScreenedAt)}`,
    ]
      .filter(Boolean)
      .join('  |  ');
    doc.text(metaLine, marginX + 14, y + 22 + entityNameExtraHeight, {
      maxWidth: contentWidth - 28,
    });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(
      ...(entity.status === 'changed' ? PDF_COLOR.primary : PDF_COLOR.muted),
    );
    doc.text(
      statusLabel(entity),
      marginX + 14,
      y + 42 + entityNameExtraHeight,
      { maxWidth: contentWidth - 28 },
    );
    y += entityCardHeight;

    if (entity.added.length) {
      autoTable(doc, {
        startY: y,
        head: [[`Added hits (${entity.added.length})`, 'Watchlist', 'Agency', 'Country']],
        body: entity.added.map((m) => [
          `${m.name || 'Unnamed record'}\nRecord: ${m.recordId}`,
          m.list || '-',
          m.source || '-',
          m.country || '-',
        ]),
        margin: { top: 58, bottom: 58, left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak', textColor: PDF_COLOR.text },
        headStyles: {
          fillColor: PDF_COLOR.green,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: { fillColor: PDF_COLOR.card },
        columnStyles: { 0: { cellWidth: 130 }, 1: { cellWidth: 190 } },
        theme: 'plain',
      });
      advanceAfterTable();
    }

    if (entity.modified.length) {
      const rows: string[][] = [];
      entity.modified.forEach((m) => {
        m.fieldChanges.forEach((fc, i) => {
          rows.push([
            i === 0 ? `${m.name || 'Unnamed record'}\nRecord: ${m.recordId}` : '',
            fieldLabel(fc.field),
            fmtVal(fc.before),
            fmtVal(fc.after),
          ]);
        });
      });
      autoTable(doc, {
        startY: y,
        head: [[`Updated hits (${entity.modified.length})`, 'Changed field', 'Before', 'After']],
        body: rows,
        margin: { top: 58, bottom: 58, left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak', textColor: PDF_COLOR.text },
        headStyles: {
          fillColor: PDF_COLOR.amber,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: { fillColor: PDF_COLOR.card },
        theme: 'plain',
      });
      advanceAfterTable();
    }

    if (entity.removed.length) {
      autoTable(doc, {
        startY: y,
        head: [[`Removed hits (${entity.removed.length})`, 'Watchlist', 'Agency', 'Country']],
        body: entity.removed.map((m) => [
          `${m.name || 'Unnamed record'}\nRecord: ${m.recordId}`,
          m.list || '-',
          m.source || '-',
          m.country || '-',
        ]),
        margin: { top: 58, bottom: 58, left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak', textColor: PDF_COLOR.text },
        headStyles: {
          fillColor: PDF_COLOR.red,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: { fillColor: PDF_COLOR.card },
        columnStyles: { 0: { cellWidth: 130 }, 1: { cellWidth: 190 } },
        theme: 'plain',
      });
      advanceAfterTable();
    }
  });

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    if (page > 1) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...PDF_COLOR.primary);
      doc.text('BITE  |  ENTITY SCREENING', marginX, 30);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...PDF_COLOR.muted);
      doc.text('Screening Changes Report', pageWidth - marginX, 30, { align: 'right' });
      doc.setDrawColor(...PDF_COLOR.divider);
      doc.setLineWidth(0.5);
      doc.line(marginX, 42, pageWidth - marginX, 42);
    }
    doc.setDrawColor(...PDF_COLOR.divider);
    doc.line(marginX, pageHeight - 35, pageWidth - marginX, pageHeight - 35);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF_COLOR.muted);
    doc.text('Confidential - generated by BITE', marginX, pageHeight - 20);
    doc.text(page === 1 ? 'Entity Screening Changes Report' : `Page ${page} of ${pages}`, pageWidth - marginX, pageHeight - 20, { align: 'right' });
  }

  return doc;
};

/** Build and download a per-entity change report as a PDF. */
export const downloadEntityChangesPdf = (
  entities: EntityChangeDetail[],
  meta: ChangeReportMeta,
  fileName: string,
): void => {
  buildEntityChangesPdf(entities, meta).save(withExtension(fileName, 'pdf'));
};

const csvEscape = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** Build and download a flat per-hit change report as a CSV (one row per change). */
export const downloadEntityChangesCsv = (
  entities: EntityChangeDetail[],
  meta: ChangeReportMeta,
  fileName: string,
): void => {
  const changedEntities = getReportableEntityChanges(entities);
  const headers = [
    'Entity',
    'Country',
    'Risk',
    'Change Type',
    'Hit Name',
    'List',
    'Source',
    'Hit Country',
    'Field',
    'Before',
    'After',
    'Compared From',
    'Compared To',
  ];

  const rows: string[][] = [];
  changedEntities.forEach((entity) => {
    const base = [entity.entityName || entity.databaseEntityId, entity.country || '', entity.riskLevel || ''];
    const tail = [fmtDate(entity.previousScreenedAt), fmtDate(entity.currentScreenedAt)];

    entity.added.forEach((m) =>
      rows.push([...base, 'Added', m.name || '', m.list || '', m.source || '', m.country || '', '', '', '', ...tail]),
    );
    entity.removed.forEach((m) =>
      rows.push([...base, 'Removed', m.name || '', m.list || '', m.source || '', m.country || '', '', '', '', ...tail]),
    );
    entity.modified.forEach((m) => {
      m.fieldChanges.forEach((fc) =>
        rows.push([
          ...base,
          'Updated',
          m.name || '',
          m.list || '',
          m.source || '',
          m.country || '',
          fc.field,
          fmtVal(fc.before),
          fmtVal(fc.after),
          ...tail,
        ]),
      );
    });
  });

  const body = [headers, ...rows]
    .map((row) => row.map((cell) => csvEscape(String(cell ?? ''))).join(','))
    .join('\r\n');
  const csv = `﻿${body}`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = withExtension(fileName, 'csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
